import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import React, { FC, useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';
import { toDisplayStatus } from '../../components/status-badge/status-badge';
import { MultiOrderDetails } from '../../graphql/fragments/MultiOrderDetails';
import { GetListOfAllCob } from '../list-of-lob/queries/all-cob-list';

const ORDER_SEARCH_BY_DATE = gql`
  query getOrdersbyDate(
    $soldToId: String!
    $startDate: DateString!
    $endDate: DateString!
    $shipToId: String
    $includeTMSOrKeplerData: Boolean
    $businessAreas: [BusinessAreaInput!]
  ) {
    ordersByDate(
      soldToId: $soldToId
      startDate: $startDate
      endDate: $endDate
      shipToId: $shipToId
      includeTMSOrKeplerData: $includeTMSOrKeplerData
      businessAreas: $businessAreas
    ) {
      ...MultiDetails
    }
  }
  ${MultiOrderDetails}
`;

const LoaderIcon = styled.div`
  border: 2px solid #f2f2f2;
  border-radius: 50%;
  border-top: 2px solid #1c6eff;
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;
  display: inline-block;
  position: relative;
  top: 4px;
  margin-right: 4px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const DownloadCSVdata: FC<{
  soldToId: string;
  shipToId?: string;
  startDate: string;
  endDate: string;
}> = ({ soldToId, shipToId, startDate, endDate }) => {
  const [csvDownloadData, setCSVDownloadData] = useState([{}]);
  const [showDownloadLink, setShowDownloadLink] = useState(false);
  const [allowDownload, setAllowDownload] = useState(true);

  const csvLink = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const headers = [
    { label: 'Ship to number', key: 'shiptonumber' },
    { label: 'Ship to name', key: 'shiptoname' },
    { label: 'Shell order number', key: 'shellordernumber' },
    { label: 'Delivery note', key: 'deliverynote' },
    { label: 'Shipment', key: 'shipment' },
    { label: 'PO number', key: 'ponumber' },
    { label: 'Created on', key: 'createdon' },
    { label: '#(line item)', key: 'lineitem' },
    { label: 'Material code', key: 'materialcode' },
    { label: 'Material description', key: 'materialdescription' },
    { label: 'Plant Name', key: 'plantname' },
    { label: 'Plant Address', key: 'plantaddress' },
    { label: 'Quantity', key: 'quantity' },
    { label: 'TMS data', key: 'tmsdata' },
    { label: 'Backordered', key: 'backordered' },
    { label: 'Unit of measure', key: 'unitofmeasure' },
    { label: 'First date', key: 'firstdate' },
    { label: 'New estimated date', key: 'newestimatedate' },
    { label: 'GSAP status', key: 'gsapstatus' },
    { label: 'Invoice', key: 'invoice' },
    { label: 'TMS delivery date', key: 'tmsdate' },
  ];

  const allCob = GetListOfAllCob();
  let cob: any[] = [];
  if (localStorage.getItem('activeCob')) {
    cob = JSON.parse(localStorage.activeCob);
  } else {
    if (localStorage.allowedCob) {
      cob = JSON.parse(localStorage.allowedCob);
    } else {
      if (allCob !== undefined) {
        cob = allCob;
      }
    }
  }

  const GetItems = () => {
    const { data } = useQuery(ORDER_SEARCH_BY_DATE, {
      variables: {
        soldToId,
        startDate,
        endDate,
        shipToId,
        includeTMSOrKeplerData: true,
        businessAreas: cob,
      },
    });
    if (data) {
      return data.ordersByDate;
    }
  };

  const items = GetItems();
  const filterByKey = (obj = {}, keyValue: string) => {
    const views: any[] = [];
    const traverse = (filterObj: { [x: string]: any }) => {
      for (const index in filterObj) {
        if (filterObj.hasOwnProperty(index)) {
          if (index === keyValue) {
            views.push(filterObj[index]);
          }
          if (!!filterObj[index] && typeof filterObj[index] === 'object') {
            traverse(filterObj[index]);
          }
        }
      }
    };
    traverse(obj);
    return views;
  };

  const itemData = () => {
    const orderData: any = [];
    for (const order of items) {
      for (const iItems of order.items) {
        if (iItems.deliveries.length > 1) {
          for (const deliveries of iItems.deliveries) {
            orderData.push({
              shiptonumber: order.shipTo.id,
              shiptoname:
                order.shipTo.address.name +
                ', ' +
                order.shipTo.address.city +
                ', ' +
                order.shipTo.address.country +
                ', ' +
                order.shipTo.address.postalCode,
              shellordernumber: order.GSAPid,
              deliverynote: deliveries.deliveryId,
              shipment: deliveries.shipment && deliveries.shipment.id,
              ponumber: order.poID,
              createdon: order.createdOn,
              lineitem: iItems.index,
              materialcode: iItems.product.id,
              materialdescription: iItems.product.description,
              plantname: iItems.plant.id,
              plantaddress: iItems.plant.description,
              quantity: deliveries.quantity,
              tmsdata: filterByKey(
                deliveries.tmsOrKeplerDeliveryEvent,
                'eventStatus'
              ).toString(),
              backordered: iItems.backorderedQuantity,
              unitofmeasure: iItems.product.unitOfMeasure,
              firstdate: filterByKey(iItems.schedules, 'promiseDate').slice(
                0,
                1
              ),
              newestimatedate: filterByKey(
                iItems.schedules,
                'promiseDate'
              ).slice(1, 2),
              gsapstatus: toDisplayStatus(iItems.status),
              invoice: deliveries.invoice && deliveries.invoice.id,
              tmsdate: filterByKey(
                deliveries.tmsOrKeplerDeliveryEvent,
                'deliveryDate'
              )
                .toString()
                .slice(0, 10),
            });
          }
        } else {
          orderData.push({
            shiptonumber: order.shipTo.id,
            shiptoname:
              order.shipTo.address.name +
              ', ' +
              order.shipTo.address.city +
              ', ' +
              order.shipTo.address.country +
              ', ' +
              order.shipTo.address.postalCode,
            shellordernumber: order.GSAPid,
            deliverynote: filterByKey(iItems.deliveries, 'deliveryId').join(
              ' & '
            ),
            shipment: filterByKey(
              filterByKey(iItems.deliveries, 'shipment'),
              'id'
            ).join(' & '),
            ponumber: order.poID,
            createdon: order.createdOn,
            lineitem: iItems.index,
            materialcode: iItems.product.id,
            materialdescription: iItems.product.description,
            plantname: iItems.plant.id,
            plantaddress: iItems.plant.description,
            quantity: iItems.desiredQuantity,
            tmsdata: filterByKey(
              filterByKey(iItems.deliveries, 'tmsOrKeplerDeliveryEvent'),
              'eventStatus'
            ).toString(),
            backordered: iItems.backorderedQuantity,
            unitofmeasure: iItems.product.unitOfMeasure,
            firstdate: filterByKey(iItems.schedules, 'promiseDate').slice(0, 1),
            newestimatedate: filterByKey(iItems.schedules, 'promiseDate').slice(
              1,
              2
            ),
            gsapstatus: toDisplayStatus(iItems.status),
            invoice: filterByKey(
              filterByKey(iItems.deliveries, 'invoice'),
              'id'
            ).join(' & '),
            tmsdate: filterByKey(
              filterByKey(iItems.deliveries, 'tmsOrKeplerDeliveryEvent'),
              'deliveryDate'
            )
              .toString()
              .slice(0, 10),
          });
        }
      }
    }
    // return orderData;
    setCSVDownloadData(orderData);
  };

  useEffect(() => {
    if (!showDownloadLink && items !== undefined) {
      setShowDownloadLink(true);
    }
    if (showDownloadLink) {
      itemData();
    }
  }, [items, showDownloadLink]);

  useEffect(() => {
    if (allowDownload && showDownloadLink) {
      setTimeout(() => {
        csvLink?.current?.link.click();
        setAllowDownload(false);
      }, 1000);
    }
  }, [allowDownload, showDownloadLink]);

  return (
    <div style={{ position: 'absolute', right: '40px' }}>
      {!showDownloadLink && (
        <span>
          <LoaderIcon /> <span> Fetching Data...</span>
        </span>
      )}
      {showDownloadLink && (
        <CSVLink
          ref={csvLink}
          data={csvDownloadData}
          headers={headers}
          filename={'soldTo-' + soldToId + '.csv'}
        >
          {showDownloadLink && 'Download CSV'}
        </CSVLink>
      )}
    </div>
  );
};
